/**
 * Deal Rooms list page - Phase 6 Shared Deal Rooms.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FolderLock, Loader2, Plus, Users, FileText } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import dealRoomService from '../services/dealRoomService';
import dealPipelineService from '../services/dealPipelineService';
import { DealRoom, DealRoomListFilters, DealRoomStatus } from '../types/dealRoom';
import { DealPipeline } from '../types/dealPipeline';

const STATUS_OPTIONS: Array<{ value: DealRoomStatus; label: string }> = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
  { value: 'archived', label: 'Archived' },
];

const statusBadge = (s: DealRoomStatus): string => {
  switch (s) {
    case 'draft':
      return 'bg-gray-100 text-gray-700 border-gray-300';
    case 'active':
      return 'bg-green-100 text-green-700 border-green-300';
    case 'closed':
      return 'bg-blue-100 text-blue-700 border-blue-300';
    case 'archived':
      return 'bg-amber-100 text-amber-700 border-amber-300';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-300';
  }
};

const formatDate = (s?: string | null): string =>
  s ? new Date(s).toLocaleDateString() : '—';

const DealRoomsPage: React.FC = () => {
  const [rooms, setRooms] = useState<DealRoom[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<DealRoomListFilters>({});
  const [pipelines, setPipelines] = useState<DealPipeline[]>([]);

  const queryFilters = useMemo(() => filters, [filters]);

  useEffect(() => {
    const load = async () => {
      const res = await dealPipelineService.fetchPipelines({ size: 200 });
      if (res.data) setPipelines(res.data.pipelines);
    };
    load();
  }, []);

  const loadRooms = async () => {
    setIsLoading(true);
    const res = await dealRoomService.fetchDealRooms({
      ...queryFilters,
      size: 200,
    });
    if (res.error) {
      toast.error(res.error);
      setRooms([]);
    } else {
      setRooms(res.data?.deal_rooms || []);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFilters]);

  const handleFilter = <K extends keyof DealRoomListFilters>(
    key: K,
    value: DealRoomListFilters[K] | ''
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' || value === undefined ? undefined : value,
    }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Deal Rooms</h1>
            <p className="text-sm text-gray-600 mt-1">
              Shared, tiered-access rooms for documents, NDA flow, and investor
              engagement tracking.
            </p>
          </div>
          <Link
            to="/deal-rooms/new"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Deal Room
          </Link>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select
              value={filters.status || ''}
              onChange={(e) =>
                handleFilter('status', e.target.value as DealRoomStatus | '')
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>

            <select
              value={filters.pipeline_id ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setFilters((prev) => ({
                  ...prev,
                  pipeline_id: v === '' ? undefined : parseInt(v, 10),
                }));
              }}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All pipelines</option>
              {pipelines.map((p) => {
                const label =
                  p.project?.project_name ||
                  p.startup?.name ||
                  (p.project_id
                    ? `Project #${p.project_id}`
                    : p.startup_id
                    ? `Startup #${p.startup_id}`
                    : `Pipeline #${p.id}`);
                const inv = p.investor?.full_name || `Investor #${p.investor_id}`;
                return (
                  <option key={p.id} value={p.id}>
                    #{p.id} · {inv} → {label}
                  </option>
                );
              })}
            </select>

            <div />
          </div>
        </div>

        {/* List */}
        {isLoading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            Loading deal rooms...
          </div>
        ) : rooms.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
            <FolderLock className="w-10 h-10 mx-auto text-blue-600 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              No deal rooms yet
            </h2>
            <p className="text-sm mb-4">
              Create one to start sharing diligence materials with investors.
            </p>
            <Link
              to="/deal-rooms/new"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create deal room
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rooms.map((r) => {
              const linkedName =
                r.project?.project_name ||
                r.startup?.name ||
                (r.project_id
                  ? `Project #${r.project_id}`
                  : r.startup_id
                  ? `Startup #${r.startup_id}`
                  : '—');
              const docCount = r.document_count ?? r.documents?.length ?? 0;
              const accessCount = r.access_count ?? r.access?.length ?? 0;
              return (
                <Link
                  key={r.id}
                  to={`/deal-rooms/${r.id}`}
                  className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <FolderLock className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {r.room_name}
                        </h3>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 truncate">
                        {linkedName}
                      </div>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium border capitalize ${statusBadge(
                        r.status
                      )}`}
                    >
                      {r.status}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 mt-3 text-xs text-gray-600">
                    <span className="inline-flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5" />
                      {docCount} doc{docCount === 1 ? '' : 's'}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {accessCount} access
                    </span>
                    {r.nda_required && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-medium">
                        NDA
                      </span>
                    )}
                  </div>

                  <div className="text-xs text-gray-400 mt-2">
                    Created {formatDate(r.created_at)}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DealRoomsPage;
